<template>
  <div class="finance-overview ls-card">
    <!-- 工具栏：刷新 + 周期切换 -->
    <div class="period-bar">
      <el-button
        type="default"
        size="small"
        icon="el-icon-refresh"
        :loading="summaryLoading || trendLoading"
        @click="loadData"
      >刷新</el-button>
      <el-radio-group v-model="period" size="small" @change="loadData">
        <el-radio-button label="week">近7天</el-radio-button>
        <el-radio-button label="month">近30天</el-radio-button>
      </el-radio-group>
    </div>

    <!-- KPI 卡片 -->
    <el-row :gutter="16" class="kpi-row" v-loading="summaryLoading">
      <el-col :span="4" v-for="card in kpiCards" :key="card.label">
        <div class="kpi-card">
          <div class="kpi-label">{{ card.label }}</div>
          <div class="kpi-value" :class="{ 'is-negative': card.negative }">
            {{ card.value }}
          </div>
          <div class="kpi-sub" v-if="card.sub">{{ card.sub }}</div>
        </div>
      </el-col>
    </el-row>

    <!-- 折线图 -->
    <div class="chart-card" v-loading="trendLoading">
      <div class="chart-title">收入与利润趋势</div>
      <div ref="chartRef" class="chart-box"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import dayjs from 'dayjs'
import { apiFinanceSummary, apiFinanceTrend } from '@/api/work_order_finance'

export default {
  name: 'FinanceOverview',
  data () {
    return {
      period: 'week',
      summaryLoading: false,
      trendLoading: false,
      summary: {
        order_count: 0,
        completed_count: 0,
        revenue_total: '0.00',
        revenue_paid: '0.00',
        cost_total: '0.00',
        profit: '0.00',
        profit_rate: 0
      },
      trendData: [],
      chart: null
    }
  },
  computed: {
    kpiCards () {
      const s = this.summary
      const profit = parseFloat(s.profit)
      return [
        { label: '新增工单', value: s.order_count + ' 笔', negative: false },
        { label: '完结工单', value: s.completed_count + ' 笔', negative: false },
        { label: '应收收入', value: '¥' + s.revenue_total, negative: false },
        { label: '实收收入', value: '¥' + s.revenue_paid, negative: false },
        { label: '总成本', value: '¥' + s.cost_total, negative: false },
        {
          label: '利润',
          value: '¥' + s.profit,
          sub: '利润率 ' + s.profit_rate + '%',
          negative: profit < 0
        }
      ]
    }
  },
  created () {
    this.loadSummary()
  },
  mounted () {
    this.chart = echarts.init(this.$refs.chartRef)
    window.addEventListener('resize', this.resizeChart)
    this.loadTrend()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeChart)
    this.chart && this.chart.dispose()
  },
  methods: {
    loadData () {
      this.loadSummary()
      this.loadTrend()
    },
    loadSummary () {
      this.summaryLoading = true
      apiFinanceSummary({ period: this.period })
        .then(data => { this.summary = data || this.summary })
        .finally(() => { this.summaryLoading = false })
    },
    loadTrend () {
      this.trendLoading = true
      apiFinanceTrend({ period: this.period })
        .then(data => {
          this.trendData = data || []
          this.renderChart()
        })
        .finally(() => { this.trendLoading = false })
    },
    renderChart () {
      if (!this.chart) return
      const dates = this.trendData.map(d => dayjs.unix(d.date).format('MM-DD'))
      const revenueTotal = this.trendData.map(d => parseFloat(d.revenue_total))
      const revenuePaid = this.trendData.map(d => parseFloat(d.revenue_paid))
      const profit = this.trendData.map(d => parseFloat(d.profit))

      this.chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['应收收入', '实收收入', '利润'] },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: dates },
        yAxis: { type: 'value', axisLabel: { formatter: '¥{value}' } },
        series: [
          {
            name: '应收收入',
            type: 'line',
            smooth: true,
            data: revenueTotal,
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '实收收入',
            type: 'line',
            smooth: true,
            data: revenuePaid,
            itemStyle: { color: '#67C23A' }
          },
          {
            name: '利润',
            type: 'line',
            smooth: true,
            data: profit,
            itemStyle: { color: '#E6A23C' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(230,162,60,0.3)' },
                { offset: 1, color: 'rgba(230,162,60,0)' }
              ])
            }
          }
        ]
      })
    },
    resizeChart () {
      this.chart && this.chart.resize()
    }
  }
}
</script>

<style lang="scss" scoped>
.finance-overview {
  padding: 20px;
}

.period-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.kpi-row {
  margin-bottom: 20px;
}

.kpi-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  text-align: center;

  .kpi-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 8px;
  }

  .kpi-value {
    font-size: 22px;
    font-weight: bold;
    color: #303133;

    &.is-negative {
      color: #F56C6C;
    }
  }

  .kpi-sub {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
  }
}

.chart-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;

  .chart-title {
    font-size: 14px;
    font-weight: bold;
    color: #303133;
    margin-bottom: 12px;
  }

  .chart-box {
    width: 100%;
    height: 320px;
  }
}
</style>
